import { UserApiResponse } from '@interfaces/userApiResponse'
import logger from '@utils/logger'

type UserDetailsResult = { isLoggedIn: boolean; user: UserApiResponse | null }

let inflight: Promise<UserDetailsResult> | null = null

/**
 * Checks whether a user is logged in and returns user data by calling the `/api/user/details/` endpoint.
 * Concurrent in-flight calls are deduplicated to a single network request.
 *
 * @returns {Promise<{ isLoggedIn: boolean; user: UserApiResponse | null }>}
 *          - `isLoggedIn`: `true` if the API returns user details (status 200), otherwise `false`.
 *          - `user`: parsed user object if logged in, else `null`.
 */
export const checkIsLoggedIn = async (): Promise<UserDetailsResult> => {
  if (typeof window === 'undefined') {
    throw new Error('This function should only be called in client.')
  }

  if (inflight) return await inflight

  inflight = (async () => {
    try {
      const resp = await fetch('/api/user/details/', {
        next: { revalidate: 0 },
        credentials: 'include',
      })
      if (resp.status === 200) {
        const userData: UserApiResponse = (await resp.json()) || {
          addresses: [],
          amazon_prime: false,
          date_of_birth: null,
          email: '',
          name: '',
          phone: '',
          gender: '',
          whatsapp_optin: false,
          is_amazon: false,
        }
        return { isLoggedIn: true, user: userData }
      } else {
        return { isLoggedIn: false, user: null }
      }
    } catch (e) {
      logger.info(e)
      return { isLoggedIn: false, user: null }
    } finally {
      inflight = null
    }
  })()

  return await inflight
}
